import React, { useState, useEffect } from 'react';
import { Tool, ToolStatus, ToolVersion, TestScenario, ToolExecuteResult } from '../../types/tool';
import * as toolService from '../../services/toolService';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco, atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';

interface ToolDetailsProps {
  tool: Tool;
  darkMode: boolean;
  initialTab?: 'overview' | 'schema' | 'examples' | 'tryit';
}

const ToolDetails: React.FC<ToolDetailsProps> = ({ tool, darkMode, initialTab }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'schema' | 'examples' | 'tryit'>(initialTab || 'overview');

  // Try It state
  const [scenarios, setScenarios] = useState<TestScenario[]>([]);
  const [params, setParams] = useState<Record<string, any>>({});
  const [executing, setExecuting] = useState(false);
  const [execResult, setExecResult] = useState<ToolExecuteResult | null>(null);
  const [execError, setExecError] = useState<string | null>(null);

  // React to initialTab prop changes (from Quick Tools bar)
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  // Load scenarios when tool changes or Try It tab opens
  useEffect(() => {
    if (activeTab === 'tryit' && tool?.name) {
      toolService.getTestScenarios(tool.name).then(setScenarios).catch(() => setScenarios([]));
      setParams({});
      setExecResult(null);
      setExecError(null);
    }
  }, [activeTab, tool?.name]);

  const applyScenario = (scenario: TestScenario) => {
    setParams({ ...scenario.parameters });
    setExecResult(null);
    setExecError(null);
  };

  const handleExecute = async () => {
    if (!tool?.name) return;
    setExecuting(true);
    setExecResult(null);
    setExecError(null);
    try {
      const result = await toolService.executeTool(tool.name, params);
      setExecResult(result);
    } catch (e: any) {
      setExecError(e?.response?.data?.detail ?? e?.message ?? 'Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const updateParam = (key: string, value: any) => {
    setParams(prev => ({ ...prev, [key]: value }));
  };

  // Format version string
  const formatVersion = (version?: ToolVersion) => {
    if (!version) return 'N/A';
    return `v${version.major}.${version.minor}.${version.patch}`;
  };

  // Format date string
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  // Get color based on status
  const getStatusColor = (status?: string) => {
    if (!status) return darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800';

    switch (status) {
      case 'stable':
        return darkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800';
      case 'beta':
        return darkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800';
      case 'development':
        return darkMode ? 'bg-yellow-800 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      case 'experimental':
        return darkMode ? 'bg-purple-800 text-purple-200' : 'bg-purple-100 text-purple-800';
      case 'deprecated':
        return darkMode ? 'bg-orange-800 text-orange-200' : 'bg-orange-100 text-orange-800';
      case 'offline':
        return darkMode ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-800';
      default:
        return darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800';
    }
  };

  const tabCls = (tab: string) => `py-2 px-4 font-medium ${
    activeTab === tab
      ? (darkMode ? 'text-blue-400 border-b-2 border-blue-400' : 'text-blue-600 border-b-2 border-blue-600')
      : (darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900')
  }`;

  const isOffline = tool.metadata.status === 'offline';
  const isRequiresSession = tool.metadata.standalone_tier === 'requires_session' && !isOffline;
  const canExecute = !isOffline && !isRequiresSession;

  return (
    <div className={`rounded-lg ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
      <div className="border-b mb-4 pb-2">
        <div className="flex space-x-4 px-4">
          <button className={tabCls('overview')} onClick={() => setActiveTab('overview')}>Overview</button>
          <button className={tabCls('schema')} onClick={() => setActiveTab('schema')}>Schema</button>
          {tool.metadata.documentation.examples.length > 0 && (
            <button className={tabCls('examples')} onClick={() => setActiveTab('examples')}>Examples</button>
          )}
          <button className={tabCls('tryit')} onClick={() => setActiveTab('tryit')}>Try It</button>
        </div>
      </div>

      <div className="px-6 pb-6">
        {activeTab === 'overview' && (
          <div>
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold">{tool.name}</h2>
              {tool.metadata.status && (
                <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(tool.metadata.status)}`}>
                  {tool.metadata.status}
                </span>
              )}
            </div>

            {/* Offline reason on overview */}
            {isOffline && tool.metadata.offline_reason && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${darkMode ? 'bg-red-900/30 text-red-300 border border-red-800' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {tool.metadata.offline_reason}
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Description</h3>
              <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                {tool.metadata.documentation.description}
              </p>
            </div>

            {tool.metadata.documentation.usage_notes && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Usage Notes</h3>
                <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                  {tool.metadata.documentation.usage_notes}
                </p>
              </div>
            )}

            {tool.metadata.documentation.limitations && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Limitations</h3>
                <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                  {tool.metadata.documentation.limitations}
                </p>
              </div>
            )}

            {tool.metadata.documentation.see_also.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">See Also</h3>
                <div className="flex flex-wrap gap-2">
                  {tool.metadata.documentation.see_also.map(related => (
                    <span key={related} className={`px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>
                      {related}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {tool.metadata.tags.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {tool.metadata.tags.map(tag => (
                    <span key={tag} className={`px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className={`p-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <h3 className="font-semibold mb-2">Properties</h3>
                <div className={`grid grid-cols-2 gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span>Category:</span>
                  <span className="font-medium">{tool.metadata.category}</span>
                  <span>Permission:</span>
                  <span className="font-medium">{tool.metadata.permission_level}</span>
                  <span>Stateful:</span>
                  <span className="font-medium">{tool.metadata.is_stateful ? 'Yes' : 'No'}</span>
                  <span>Uses Cache:</span>
                  <span className="font-medium">{tool.metadata.use_cache ? 'Yes' : 'No'}</span>
                </div>
              </div>

              <div className={`p-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <h3 className="font-semibold mb-2">Version Information</h3>
                <div className={`grid grid-cols-2 gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span>Version:</span>
                  <span className="font-medium">{formatVersion(tool.metadata.version)}</span>
                  {tool.metadata.min_api_version && (<><span>Min API Version:</span><span className="font-medium">{tool.metadata.min_api_version}</span></>)}
                  {tool.metadata.created_at && (<><span>Created:</span><span className="font-medium">{formatDate(tool.metadata.created_at)}</span></>)}
                  {tool.metadata.updated_at && (<><span>Updated:</span><span className="font-medium">{formatDate(tool.metadata.updated_at)}</span></>)}
                  {tool.metadata.maintainer && (<><span>Maintainer:</span><span className="font-medium">{tool.metadata.maintainer}</span></>)}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'schema' && (
          <div>
            <h3 className="text-lg font-semibold mb-2">Tool Schema</h3>
            <div className={`rounded ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
              <SyntaxHighlighter language="json" style={darkMode ? atomOneDark : docco} customStyle={{ borderRadius: '0.375rem' }}>
                {JSON.stringify(tool.schema, null, 2)}
              </SyntaxHighlighter>
            </div>
          </div>
        )}

        {activeTab === 'examples' && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Examples</h3>
            {tool.metadata.documentation.examples.map((example, index) => (
              <div key={index} className={`mb-4 p-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <h4 className="font-medium mb-2">Example {index + 1}</h4>
                <SyntaxHighlighter language="json" style={darkMode ? atomOneDark : docco} customStyle={{ borderRadius: '0.375rem' }}>
                  {JSON.stringify(example, null, 2)}
                </SyntaxHighlighter>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'tryit' && (
          <div className="space-y-4">
            {/* OFFLINE banner */}
            {isOffline && (
              <div className={`p-3 rounded-lg text-sm ${darkMode ? 'bg-red-900/30 text-red-300 border border-red-800' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                Offline: {tool.metadata.offline_reason || 'This tool is currently unavailable'}
              </div>
            )}

            {/* requires_session banner */}
            {isRequiresSession && (
              <div className={`p-3 rounded-lg text-sm ${darkMode ? 'bg-amber-900/30 text-amber-300 border border-amber-800' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                This tool requires an active agent session. Test it from within a persona chat.
              </div>
            )}

            {/* Scenario cards */}
            {scenarios.length > 0 && canExecute && (
              <div>
                <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Test Scenarios</p>
                <div className="flex flex-wrap gap-2">
                  {scenarios.map((s) => (
                    <button key={s.name} onClick={() => applyScenario(s)}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                        darkMode ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-300 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span className="font-medium">{s.name}</span>
                      <span className={`ml-1.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>— {s.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Parameter form */}
            {canExecute && (
              <div className="space-y-3">
                <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Parameters</p>
                {Object.entries(tool.schema?.function?.parameters?.properties || {}).map(([key, prop]: [string, any]) => {
                  const isRequired = (tool.schema?.function?.parameters?.required || []).includes(key);
                  return (
                    <div key={key}>
                      <label className={`block text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {key}{isRequired && <span className="text-red-400 ml-0.5">*</span>}
                        {prop.description && <span className="ml-1 opacity-60">— {prop.description}</span>}
                      </label>
                      {prop.enum ? (
                        <select value={params[key] ?? ''} onChange={e => updateParam(key, e.target.value)}
                          className={`w-full px-3 py-1.5 rounded-md border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
                          <option value="">Select...</option>
                          {prop.enum.map((v: string) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      ) : prop.type === 'boolean' ? (
                        <input type="checkbox" checked={!!params[key]} onChange={e => updateParam(key, e.target.checked)} />
                      ) : prop.type === 'number' || prop.type === 'integer' ? (
                        <input type="number" value={params[key] ?? ''} onChange={e => updateParam(key, parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-1.5 rounded-md border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                      ) : typeof params[key] === 'object' ? (
                        <textarea value={JSON.stringify(params[key], null, 2)} rows={4}
                          onChange={e => { try { updateParam(key, JSON.parse(e.target.value)); } catch { /* invalid JSON, ignore */ } }}
                          className={`w-full px-3 py-1.5 rounded-md border text-sm font-mono ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                      ) : (
                        <textarea value={params[key] ?? ''} rows={prop.description?.length > 50 ? 3 : 1}
                          onChange={e => updateParam(key, e.target.value)}
                          className={`w-full px-3 py-1.5 rounded-md border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                      )}
                    </div>
                  );
                })}

                <button onClick={handleExecute} disabled={executing}
                  className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                    executing ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  {executing ? 'Executing...' : 'Execute'}
                </button>
              </div>
            )}

            {/* Results */}
            {execResult && (
              <div className={`rounded-lg border p-4 ${
                execResult.success
                  ? darkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'
                  : darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`text-xs font-medium ${execResult.success ? 'text-green-400' : 'text-red-400'}`}>
                    {execResult.success ? 'Success' : 'Failed'}
                  </span>
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {(execResult.execution_time ?? 0).toFixed(3)}s
                    {execResult.cached && ' (cached)'}
                  </span>
                </div>
                {execResult.warning && (
                  <p className={`text-xs mb-2 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{execResult.warning}</p>
                )}
                <pre className={`text-xs overflow-auto max-h-64 p-2 rounded ${darkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                  {JSON.stringify(execResult.success ? execResult.result : execResult.error, null, 2)}
                </pre>
              </div>
            )}
            {execError && (
              <div className={`rounded-lg border p-3 text-sm ${darkMode ? 'bg-red-900/30 text-red-300 border-red-800' : 'bg-red-50 text-red-700 border-red-200'}`}>
                {execError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolDetails;
